import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Tooltip
} from '@mui/material';
import { Search, Visibility, PersonAdd } from '@mui/icons-material';
import { apiService } from '../services/api';
import { User } from '../types';

interface CustomerSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onViewProfile: (customerId: string) => void;
}

interface SearchParams {
  lastName: string;
  address: string;
  zipCode: string;
  phone: string;
}

const CustomerSearchDialog: React.FC<CustomerSearchDialogProps> = ({
  open,
  onClose,
  onViewProfile
}) => {
  const [searchParams, setSearchParams] = useState<SearchParams>({
    lastName: '',
    address: '',
    zipCode: '',
    phone: ''
  });
  const [customers, setCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Clear search fields when dialog opens
  useEffect(() => {
    if (open) {
      setSearchParams({
        lastName: '',
        address: '',
        zipCode: '',
        phone: ''
      });
      setCustomers([]);
      setError(null);
    }
  }, [open]);

  const handleSearch = async () => {
    console.log('[CUSTOMER-SEARCH] ⚡ Search started');
    setLoading(true);
    setError(null);

    try {
      // Build query params
      const queryParams = new URLSearchParams();
      Object.entries(searchParams).forEach(([key, value]) => {
        if (value.trim()) {
          queryParams.append(key, value.trim());
        }
      });

      const queryString = queryParams.toString();
      console.log('[CUSTOMER-SEARCH] 📡 Fetching:', `/customers/search?${queryString}`);

      const response = await apiService.get(`/customers/search?${queryString}`);
      
      console.log('[CUSTOMER-SEARCH] ✅ Response received:', response);
      console.log('[CUSTOMER-SEARCH] Response type:', typeof response);
      console.log('[CUSTOMER-SEARCH] Response keys:', Object.keys(response || {}));
      
      // apiService.get returns response.data from axios, so response is already the API response object
      // Expected structure: {success: true, data: {customers: [...], total: ...}}
      let customers: any[] = [];
      
      const responseData = response as any;
      
      if (responseData && responseData.success && responseData.data) {
        customers = (responseData.data as any).customers || [];
        console.log('[CUSTOMER-SEARCH] ✅ Found customers via success.data.customers:', customers.length);
      } else if (responseData && (responseData as any).customers !== undefined) {
        customers = (responseData as any).customers || [];
        console.log('[CUSTOMER-SEARCH] ✅ Found customers via response.customers:', customers.length);
      } else if (Array.isArray(responseData)) {
        customers = responseData;
        console.log('[CUSTOMER-SEARCH] ✅ Found customers as array:', customers.length);
      } else {
        console.warn('[CUSTOMER-SEARCH] ⚠️ Unexpected response structure:', JSON.stringify(responseData, null, 2));
      }
      
      console.log('[CUSTOMER-SEARCH] 📊 Total customers:', customers.length);
      if (customers.length > 0) {
        const firstCustomer = customers[0];
        console.log('[CUSTOMER-SEARCH] 👤 First customer full object:', JSON.stringify(firstCustomer, null, 2));
        console.log('[CUSTOMER-SEARCH] 🏠 First customer address:', firstCustomer.address);
        console.log('[CUSTOMER-SEARCH] 🔍 Address type:', typeof firstCustomer.address);
        console.log('[CUSTOMER-SEARCH] 🔍 Address value:', firstCustomer.address);
        console.log('[CUSTOMER-SEARCH] ✅ First customer isOnboarded:', firstCustomer.isOnboarded);
        console.log('[CUSTOMER-SEARCH] 📋 First customer onboardingDetails:', firstCustomer.onboardingDetails);
      } else {
        console.warn('[CUSTOMER-SEARCH] ⚠️ No customers found in response');
      }
      
      setCustomers(customers);
      
      // Clear search fields after successful search
      setSearchParams({
        lastName: '',
        address: '',
        zipCode: '',
        phone: ''
      });
    } catch (err: any) {
      console.error('[CUSTOMER-SEARCH] ❌ Error:', err);
      console.error('[CUSTOMER-SEARCH] Error response:', err.response);
      console.error('[CUSTOMER-SEARCH] Error status:', err.response?.status);
      console.error('[CUSTOMER-SEARCH] Error data:', err.response?.data);
      
      setError(err.response?.data?.error || err.message || 'Failed to search customers');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSearchParams({
      lastName: '',
      address: '',
      zipCode: '',
      phone: ''
    });
    setCustomers([]);
    setError(null);
  };

  const formatAddress = (address: any) => {
    console.log('[CUSTOMER-SEARCH] Formatting address:', address);
    if (!address) return 'N/A';
    
    // Handle both object and string formats
    if (typeof address === 'string') {
      return address;
    }
    
    // Build address string from parts
    const parts = [];
    if (address.street) parts.push(address.street);
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.zipCode) parts.push(address.zipCode);
    
    return parts.length > 0 ? parts.join(', ') : 'N/A';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Search />
          <Typography variant="h6">Search Customers</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box mb={3}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Search for customers by last name, address, zip code, or phone number
          </Typography>
          
          <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={2}>
            <TextField
              label="Last Name"
              value={searchParams.lastName}
              onChange={(e) => setSearchParams({ ...searchParams, lastName: e.target.value })}
              size="small"
            />
            <TextField
              label="Address"
              value={searchParams.address}
              onChange={(e) => setSearchParams({ ...searchParams, address: e.target.value })}
              size="small"
            />
            <TextField
              label="Zip Code"
              value={searchParams.zipCode}
              onChange={(e) => setSearchParams({ ...searchParams, zipCode: e.target.value })}
              size="small"
            />
            <TextField
              label="Phone Number"
              value={searchParams.phone}
              onChange={(e) => setSearchParams({ ...searchParams, phone: e.target.value })}
              size="small"
            />
          </Box>
          
          <Box mt={2} display="flex" gap={1}>
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Search />}
            >
              Search
            </Button>
            <Button variant="outlined" onClick={handleClear}>
              Clear
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {customers.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Search Results ({customers.length})
            </Typography>
            
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Joined</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {customers.map((customer) => {
                  const isOnboarded = customer.isOnboarded || false;
                  const onboardingDetails = customer.onboardingDetails;
                  
                  return (
                    <TableRow key={customer._id || customer.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {customer.firstName} {customer.lastName}
                          </Typography>
                          {isOnboarded && (
                            <Tooltip 
                              title={
                                <Box>
                                  <Typography variant="caption" display="block" fontWeight="bold">
                                    Onboarded Customer
                                  </Typography>
                                  {onboardingDetails?.serviceSchedule?.frequency && (
                                    <Typography variant="caption" display="block">
                                      Frequency: {onboardingDetails.serviceSchedule.frequency}
                                    </Typography>
                                  )}
                                  {onboardingDetails?.linkedAt && (
                                    <Typography variant="caption" display="block">
                                      Linked: {formatDate(onboardingDetails.linkedAt.toString())}
                                    </Typography>
                                  )}
                                  {onboardingDetails?.importedAt && (
                                    <Typography variant="caption" display="block">
                                      Imported: {formatDate(onboardingDetails.importedAt.toString())}
                                    </Typography>
                                  )}
                                </Box>
                              }
                              arrow
                            >
                              <Chip
                                icon={<PersonAdd />}
                                label="Onboarded"
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{customer.email || 'N/A'}</TableCell>
                      <TableCell>{customer.phone || 'N/A'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200 }}>
                          {(() => {
                            const address = customer.address;
                            console.log('[CUSTOMER-TABLE] 🏠 Rendering address for:', customer.firstName, customer.lastName);
                            console.log('[CUSTOMER-TABLE] 📦 Address value:', address);
                            console.log('[CUSTOMER-TABLE] 📦 Is onboarded:', customer.isOnboarded);
                            const formatted = formatAddress(address);
                            console.log('[CUSTOMER-TABLE] ✨ Formatted result:', formatted);
                            return formatted;
                          })()}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(customer.createdAt.toString())}</TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => {
                            const customerId = customer._id || customer.id;
                            if (customerId) {
                              onViewProfile(customerId);
                            }
                          }}
                          color="primary"
                          title="View Profile"
                        >
                          <Visibility />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}

        {customers.length === 0 && !loading && (
          <Box textAlign="center" py={4}>
            <Typography variant="body2" color="text.secondary">
              No customers found. Try adjusting your search criteria.
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerSearchDialog;
